import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';
import { getPaymentsByBorrowerBulk } from '../../services/request/Payments';
import { formatCurrency } from '../../utils/CurrencyFormat';
import {
  FrequencyPayment,
  buildMonthlyFrequency,
  computeFrequencyStats,
  formatDays,
  gapHistogram,
  isCollectionPayment,
} from '../../utils/paymentFrequency';
import { EmptyState, MetricStrip, SectionPanel } from '../../shared-components/ui';
import { toast } from 'react-toastify';

interface PaymentFrequencyPanelProps {
  borrowerId: string;
}

const PaymentFrequencyPanel: React.FC<PaymentFrequencyPanelProps> = ({ borrowerId }) => {
  const [payments, setPayments] = useState<FrequencyPayment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [collectionsOnly, setCollectionsOnly] = useState(true);

  const load = useCallback(async () => {
    if (!borrowerId) return;
    setLoading(true);
    try {
      const res = await getPaymentsByBorrowerBulk(borrowerId, 500);
      setPayments(res.result ?? []);
      setTotalCount(res.totalCount ?? 0);
    } catch {
      toast.error('Failed to load payment frequency data');
      setPayments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [borrowerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const scoped = useMemo(
    () => (collectionsOnly ? payments.filter(isCollectionPayment) : payments),
    [payments, collectionsOnly]
  );

  const monthly = useMemo(() => buildMonthlyFrequency(scoped), [scoped]);
  const stats = useMemo(() => computeFrequencyStats(payments), [payments]);
  const gaps = useMemo(() => gapHistogram(payments), [payments]);

  if (loading && payments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Based on {scoped.length} payment{scoped.length === 1 ? '' : 's'}
          {totalCount > payments.length ? ` (loaded ${payments.length} of ${totalCount})` : ''}
          {collectionsOnly ? ' · TOP_UP excluded from cadence' : ''}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={collectionsOnly}
                onChange={(_, v) => setCollectionsOnly(v)}
              />
            }
            label={<Typography variant="body2">Collections only</Typography>}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => void load()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <MetricStrip
        columns={{ xs: 12, sm: 6, md: 3 }}
        metrics={[
          {
            label: 'Total payments',
            value: stats.totalCount,
            hint: formatCurrency(stats.totalAmount),
            tone: 'info',
          },
          {
            label: 'This month',
            value: stats.thisMonthCount,
            hint: formatCurrency(stats.thisMonthAmount),
            tone: 'success',
          },
          {
            label: 'Avg gap',
            value: formatDays(stats.avgDaysBetween),
            hint: `Median ${formatDays(stats.medianDaysBetween)}`,
          },
          {
            label: 'Last payment',
            value: stats.lastPaymentDate || '—',
            hint: 'Most recent capture',
          },
        ]}
      />

      {monthly.length === 0 ? (
        <EmptyState
          title="No payments yet"
          description="Capture interest or repayments on this customer’s debits to see frequency trends."
        />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <SectionPanel
              title="Payments per month"
              subtitle="How often this customer pays"
            >
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                      yAxisId="count"
                      allowDecimals={false}
                      tick={{ fontSize: 11 }}
                      width={36}
                    />
                    <YAxis
                      yAxisId="amount"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      width={56}
                      tickFormatter={(v) =>
                        Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(1)}L` : `₹${v}`
                      }
                    />
                    <ReTooltip
                      formatter={(value: number, name: string) =>
                        name === 'Amount'
                          ? [formatCurrency(value), name]
                          : [value, name]
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="count"
                      dataKey="count"
                      name="Payments"
                      fill="#0F766E"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={36}
                    />
                    <Line
                      yAxisId="amount"
                      type="monotone"
                      dataKey="amount"
                      name="Amount"
                      stroke="#D97706"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </SectionPanel>
          </Grid>

          <Grid item xs={12} lg={5}>
            <SectionPanel
              title="Days between payments"
              subtitle="Gap buckets (collections only)"
            >
              <Box sx={{ height: 300, width: '100%' }}>
                {gaps.every((g) => g.count === 0) ? (
                  <EmptyState
                    title="Need 2+ collection payments"
                    description="Gap analysis needs at least two interest/repayment events."
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={gaps} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                      <ReTooltip />
                      <Bar dataKey="count" name="Occurrences" fill="#0369A1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </SectionPanel>
          </Grid>

          <Grid item xs={12}>
            <SectionPanel
              title="Amount by type"
              subtitle="Stacked monthly volume (INR)"
            >
              <Box sx={{ height: 320, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      width={56}
                      tickFormatter={(v) =>
                        Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(1)}L` : `₹${v}`
                      }
                    />
                    <ReTooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="interest" name="Interest" stackId="a" fill="#0F766E" />
                    <Bar dataKey="repayment" name="Repayment" stackId="a" fill="#15803D" />
                    <Bar dataKey="lateFee" name="Late fee" stackId="a" fill="#B91C1C" />
                    {!collectionsOnly && (
                      <Bar dataKey="topUp" name="Top-up" stackId="a" fill="#0369A1" />
                    )}
                    <Bar dataKey="other" name="Other" stackId="a" fill="#64748B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </SectionPanel>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PaymentFrequencyPanel;
